import { useEffect } from 'react';
import { ContactTooltip } from 'components/ContactTooltip';
import {
  WARNING_DAYS,
  Item as ItemBar,
  RowCell,
} from '@centerpiece/components';
import { ListSubheader, ListItem } from '@mui/material';
import moment from 'moment';
import {
  ITEMSTATUSES,
  Project,
  ProjectContact,
  Relationship,
} from 'store/api/models';
import { useNavigate } from 'react-router-dom';
import { getItemColors } from '@centerpiece/components/src/utils/item.utils';
import { getItemDates } from './project.utils';
import { useIndexContext } from 'contexts/index.context';

export const useProjectsIndex = ({
  projects = [],
  setProjectIndex,
}: {
  projects: any[];
  setProjectIndex: React.Dispatch<React.SetStateAction<{ [key: number]: any }>>;
}) => {
  const { contacts, roles } = useIndexContext();
  return useEffect(() => {
    setProjectIndex(projectIndex => {
      // let cpyProjectIndex = { ...projectIndex };
      let cpyProjectIndex: { [key: number]: any } = {};
      // only copy over counts, msgs, and cmts, risk and child
      // incase things arrive out of order
      Object.keys(projectIndex).forEach((key: string) => {
        cpyProjectIndex[parseInt(key)] = {
          counts: projectIndex?.[parseInt(key)]?.counts && {
            ...projectIndex?.[parseInt(key)]?.counts,
          },
          msgs: projectIndex?.[parseInt(key)]?.msgs && {
            ...projectIndex?.[parseInt(key)]?.msgs,
          },
          cmts: projectIndex?.[parseInt(key)]?.cmts && {
            ...projectIndex?.[parseInt(key)]?.cmts,
          },
          risk: projectIndex?.[parseInt(key)]?.risk && {
            ...projectIndex?.[parseInt(key)]?.risk,
          },
          child: projectIndex?.[parseInt(key)]?.child && {
            ...projectIndex?.[parseInt(key)]?.child,
          },
          next_item: projectIndex?.[parseInt(key)]?.next_item && {
            ...projectIndex?.[parseInt(key)]?.next_item,
          },
          next_item_due: projectIndex?.[parseInt(key)]?.next_item_due && {
            ...projectIndex?.[parseInt(key)]?.next_item_due,
          },
        };
      });
      projects?.forEach(project => {
        const nextStage = [
          ...project.stages?.filter((s: any) => !s.outcome),
        ].sort((a, b) => a.sequence - b.sequence)?.[0];
        cpyProjectIndex[project.id] = {
          ...(cpyProjectIndex[project.id] || {}),
          ...project,
          title: {
            tooltip: project?.form?.address
              ? `${project?.form?.address || ''} ${project?.form?.city || ''} ${
                  project?.form?.country !== 'CA'
                    ? `${project?.form?.state || ''}`
                    : `${project?.form?.province || ''}`
                } ${project?.form?.zipcode || ''} ${
                  project?.form?.country || ''
                }`
              : 'No location specified',
            text: project.title,
          },
          stage: nextStage?.name,
          due: nextStage?.due_date,
          stage_due: {
            title: nextStage?.name,
            due: nextStage?.due_date,
          },
          financials: {
            text: project?.form?.revenue
              ? `$${numFormatter(project?.form?.revenue)} ${
                  project?.form?.margin
                    ? `(${Number(project?.form?.margin).toFixed(0)}%)`
                    : ''
                }`
              : undefined,
            sort: project?.form?.revenue
              ? Number(project?.form?.revenue?.replaceAll(',', ''))
              : undefined,
          },
          type_name: project?.type_r?.name,
          teams: project.teams,
          team: {
            data: project?.contacts
              ?.filter(
                (c: ProjectContact) =>
                  !c.role ||
                  (roles[c?.role]?.type !== 'External' &&
                    !c?.form?.stakeholdertype)
              )
              .map((c: ProjectContact) => ({
                contact_r: contacts[c.contact]
                  ? { ...contacts[c.contact] }
                  : undefined,
                role_r: undefined,
              }))
              ?.filter((c: ProjectContact) => c.contact_r),
            tooltip: ContactTooltip,
            team: project?.contacts
              ?.filter(
                (c: ProjectContact) =>
                  !c.role ||
                  (roles[c?.role]?.type !== 'External' &&
                    !c?.form?.stakeholdertype)
              )
              ?.map((c: ProjectContact) => ({
                contact_r: contacts[c.contact]
                  ? { ...contacts[c.contact] }
                  : undefined,
                role_r: undefined,
              }))
              ?.filter((c: ProjectContact) => c.contact_r)
              .map(
                (c: ProjectContact) =>
                  `${c.contact_r?.firstname} ${c.contact_r?.lastname}`
              ),
          },
        };
      });

      return cpyProjectIndex;
    });
  }, [projects, contacts, roles]);
};

export const useNextItems = ({
  nextItems = [],
  setProjectIndex,
}: {
  nextItems: any[];
  setProjectIndex: React.Dispatch<React.SetStateAction<{ [key: number]: any }>>;
}) => {
  return useEffect(() => {
    setProjectIndex(projectIndex => {
      let cpyProjectIndex = { ...projectIndex };
      Object.keys(cpyProjectIndex).forEach((key: string) => {
        delete cpyProjectIndex[parseInt(key)].next_item;
        delete cpyProjectIndex[parseInt(key)].next_item_due;
      });

      nextItems?.forEach(nextItem => {
        let update = {
          next_item: nextItem?.task?.title || 'No Pending Dates',
          next_item_due: {
            due: nextItem?.due_date,
            title: nextItem?.task?.title,
          },
        };
        cpyProjectIndex[nextItem.project] = {
          ...(cpyProjectIndex[nextItem.project] || {}),
          ...update,
        };
      });

      return cpyProjectIndex;
    });
  }, [nextItems]);
};

export const useItemsAtRisk = ({
  riskItems = [],
  setProjectIndex,
}: {
  riskItems: any[];
  setProjectIndex: React.Dispatch<React.SetStateAction<{ [key: number]: any }>>;
}) => {
  const { contacts } = useIndexContext();
  const navigate = useNavigate();
  return useEffect(() => {
    setProjectIndex(projectIndex => {
      let cpyProjectIndex = { ...projectIndex };
      Object.keys(cpyProjectIndex).forEach((key: string) => {
        delete cpyProjectIndex[parseInt(key)].risk;
        delete cpyProjectIndex[parseInt(key)].child;
      });

      riskItems?.forEach(item => {
        let projectRiskItems: any = [
          ...(projectIndex[item.project]?.riskItems || []),
        ];
        if (item.messages?.length > 0) {
          projectRiskItems = [
            ...projectRiskItems,
            ...item.messages.map((m: any) => ({
              ...item,
              title: item?.task?.title,
              due_date: m.due_date,
              type: 'resp_due',
              icons: ['resp_due'],
              hash: 'i',
            })),
          ];
        }

        if (item.appointments?.length > 0) {
          projectRiskItems = [
            ...projectRiskItems,
            ...item.appointments.map((a: any) => ({
              ...item,
              title: item?.task?.title,
              due_date: a.end_time,
              type: 'event',
              icons: ['event'],
              hash: 'i',
            })),
          ];
        }

        if (item.task?.due_date) {
          projectRiskItems = [
            ...projectRiskItems,
            {
              ...item,
              title: item?.task?.title,
              due_date: item?.task?.due_date,
              type: 'task',
              icons: ['task'],
              hash: 'i',
            },
          ];
        }

        projectRiskItems.sort((a: any, b: any) =>
          moment(a.due_date).diff(b.due_date)
        );

        const itemsPastDue: any[] = projectRiskItems?.filter(
          (item: any) =>
            item?.due_date &&
            moment(item?.due_date).diff(moment.now(), 'days') < 0
        );
        const numItemsPastDue: number = itemsPastDue?.length;
        const itemsAtRisk: any[] = projectRiskItems?.filter(
          (item: any) =>
            item?.due_date &&
            moment(item?.due_date).diff(moment.now(), 'days') <= WARNING_DAYS &&
            item?.due_date &&
            moment(item?.due_date).diff(moment.now(), 'days') >= 0
        );
        const numItemsAtRisk: number = itemsAtRisk?.length;

        let update = {
          risk: { warning: numItemsAtRisk, error: numItemsPastDue },
          ...([...(itemsPastDue || []), ...(itemsAtRisk || [])].length > 0
            ? {
                child: {
                  subheader: (
                    <ListSubheader
                      component="div"
                      id="nested-list-subheader"
                      sx={{
                        backgroundColor: 'transparent',
                        fontWeight: 800,
                        textDecoration: 'underline',
                      }}
                    >
                      Items at Risk:
                    </ListSubheader>
                  ),
                  content: [...itemsPastDue, ...itemsAtRisk].map(
                    (item, index) => {
                      return (
                        <ListItem
                          key={index}
                          onClick={() =>
                            navigate(
                              `/project/${item.project}/#i=${item.id}&at=info`
                            )
                          }
                          sx={{ cursor: 'pointer' }}
                        >
                          <ItemBar
                            title={item.title || ''}
                            // icon={(item as any).type}
                            icons={item.icons as any}
                            disabled={true}
                            due_date={new Date(item.due_date)}
                            dates={[
                              { type: item.type, end: item.due_date } as any,
                            ]}
                            complete={item.status === ITEMSTATUSES.COMPLETE}
                            assignees={{
                              data: (item as any)?.relationships?.map(
                                (c: any) => {
                                  const owner = contacts[c.contact];
                                  return {
                                    id: c.id,
                                    contact_r: owner,
                                  };
                                }
                              ),
                              tooltip: ContactTooltip,
                            }}
                          />
                        </ListItem>
                      );
                    }
                  ),
                },
              }
            : {}),
        };
        cpyProjectIndex[item.project] = {
          ...(cpyProjectIndex[item.project] || {}),
          ...update,
        };
      });

      return cpyProjectIndex;
    });
  }, [riskItems, contacts]);
};

export const useItemCounts = ({
  itemCounts = [],
  setProjectIndex,
}: {
  itemCounts: any[];
  setProjectIndex: React.Dispatch<React.SetStateAction<{ [key: number]: any }>>;
}) => {
  return useEffect(() => {
    setProjectIndex(projectIndex => {
      let cpyProjectIndex = { ...projectIndex };
      Object.keys(cpyProjectIndex).forEach((key: string) => {
        delete cpyProjectIndex[parseInt(key)].counts;
      });
      itemCounts?.forEach(item => {
        let update = {
          counts: {
            percent: Math.round(
              (100 *
                (item?.item_counts?.find(
                  (i: any) => i.status_r.name === 'Complete'
                )?.count || 0)) /
                item?.item_counts?.reduce(
                  (acc: number, b: any) => acc + b.count,
                  0
                )
            ),
          },
        };
        cpyProjectIndex[item.project] = {
          ...(cpyProjectIndex[item.project] || {}),
          ...update,
        };
      });

      return cpyProjectIndex;
    });
  }, [itemCounts]);
};

export const useProjectsMsgs = ({
  msgs = [],
  setProjectIndex,
}: {
  msgs: any[];
  setProjectIndex: React.Dispatch<React.SetStateAction<{ [key: number]: any }>>;
}) => {
  const navigate = useNavigate();
  return useEffect(() => {
    setProjectIndex(projectIndex => {
      let cpyProjectIndex = { ...projectIndex };
      Object.keys(cpyProjectIndex).forEach((key: string) => {
        delete cpyProjectIndex[parseInt(key)].msgs;
      });
      msgs?.forEach(msg => {
        let update = {
          // ...(cpyProjectIndex[msg.project]?.msgs || {}),
          msgs: {
            num: (cpyProjectIndex[msg.project]?.msgs?.num || 0) + 1,
            onClick: () =>
              navigate(
                `/project/${msg.project}/#i=${msg.item}&at=${
                  msg.type === 'Default' ? 'message' : 'event'
                }`
              ),
          },
        };
        cpyProjectIndex[msg.project] = {
          ...(cpyProjectIndex[msg.project] || {}),
          ...update,
        };
      });

      return cpyProjectIndex;
    });
  }, [msgs]);
};

export const useProjectsCmts = ({
  cmts = [],
  setProjectIndex,
}: {
  cmts: any[];
  setProjectIndex: React.Dispatch<React.SetStateAction<{ [key: number]: any }>>;
}) => {
  const navigate = useNavigate();
  return useEffect(() => {
    if (cmts.length > 0) {
      setProjectIndex(projectIndex => {
        let cpyProjectIndex = { ...projectIndex };
        Object.keys(cpyProjectIndex).forEach((key: string) => {
          delete cpyProjectIndex[parseInt(key)].cmts;
        });
        cmts?.forEach(cmt => {
          let update = {
            // ...(cpyProjectIndex[cmt.project]?.msgs || {}),
            cmts: {
              num: (cpyProjectIndex[cmt.project]?.cmts?.num || 0) + 1,
              onClick: () =>
                navigate(`/project/${cmt.project}/#i=${cmt.item}&at=comment`),
            },
          };
          cpyProjectIndex[cmt.project] = {
            ...(cpyProjectIndex[cmt.project] || {}),
            ...update,
          };
        });

        return cpyProjectIndex;
      });
    }
  }, [cmts]);
};

export const useItemIndex = ({
  relationships = [],
  setItemIndex,
}: {
  relationships: any[];
  setItemIndex: React.Dispatch<React.SetStateAction<{ [key: number]: any }>>;
}) => {
  const navigate = useNavigate();
  const { contacts } = useIndexContext();
  useEffect(() => {
    setItemIndex(itemIndex => {
      let cpyItemIndex: { [key: number]: any } = {};
      // only copy over counts, msgs, and cmts, risk and child
      Object.keys(itemIndex).forEach((key: string) => {
        cpyItemIndex[parseInt(key)] = {
          msgs: itemIndex?.[parseInt(key)]?.msgs && {
            ...itemIndex?.[parseInt(key)]?.msgs,
          },
          cmts: itemIndex?.[parseInt(key)]?.msgs && {
            ...itemIndex?.[parseInt(key)]?.cmts,
          },
        };
      });

      let toDos: RowCell[] = [];

      relationships?.forEach(rel => {
        const relationship = {
          ...rel,
          // owner: contacts[rel.contact],
          contact_r: {
            ...contacts[rel.contact],
            role_r: undefined,
            contact_r: { ...contacts[rel.contact], role_r: undefined },
          },
        };
        let project: Project = relationship.project_r;
        let nestedRelationships: Relationship[] =
          relationship.relationships.map((r: any) => ({
            ...r,
            // owner: contacts[r.contact],
            contact_r: {
              ...contacts[r.contact],
              role_r: undefined,
              contact_r: { ...contacts[r.contact], role_r: undefined },
            },
          }));
        let icons = [];
        if (
          !!relationship?.coordination_r ||
          relationship?.form?.tabs?.message?.visible
        ) {
          icons.push('coordination');
        }
        if (
          !!relationship?.event_r ||
          relationship?.form?.tabs?.schedule?.visible ||
          relationship?.form?.tabs?.event?.visible
        ) {
          icons.push('event');
        }
        if (icons.length === 0) {
          //always has info at least
          icons.push('task');
        }

        const dates = getItemDates({
          ...(relationship || {}),
          id: relationship?.item || 0,
        } as any);

        const todo = {
          ...relationship,
          id: relationship.item,
          icons: icons,
          item_type: 'item',
          status: relationship?.status_r?.name,
          title: relationship?.task_r?.title,
          hoverTitle: {
            tooltip: relationship?.task_r?.details,
            text: relationship?.task_r?.title,
          },
          team: {
            data: nestedRelationships?.map(c => ({
              ...c.contact_r,
              role_r: undefined,
            })),
            team: nestedRelationships?.map(
              (c: any) => `${c?.contact_r?.firstname} ${c?.contact_r?.lastname}`
            ),
            tooltip: ContactTooltip,
          },
          project_title: {
            text: project?.title,
            project_title: project?.title,
            onClick: () => navigate(`/project/${project?.id}`),
          },
          due: relationship?.task_r?.due_date,
          dates: {
            due: dates?.[0]?.end,
            dates: dates,
            // setDateExpand: setDateExpand,
            complete: relationship?.task_r?.status === ITEMSTATUSES.COMPLETE,
          },
          // msgs: msgs,
        } as RowCell;
        const {
          item: {
            border: borderColor,
            background: backgroundColor,
            font: fontColor,
          },
        } = getItemColors({
          date: dates?.[0]?.end || dates?.[0]?.start,
          complete: relationship?.task_r?.status === ITEMSTATUSES.COMPLETE,
        });
        cpyItemIndex[todo.id] = {
          ...(cpyItemIndex[todo.id] || {}),
          ...todo,
          type: {
            icons,
            type: icons.map(str => {
              return str.charAt(0).toUpperCase() + str.slice(1);
            }),
          },
          itemBorder: {
            border: borderColor,
            background: backgroundColor,
            font: fontColor,
          },
          chipStatus: {
            disabled: true,
            label: relationship?.status_r?.name,
            color: borderColor,
          },
        };
        // if (
        //   activeItem?.id === todo?.id &&
        //   activeItem?.item_type === todo?.item_type
        // ) {
        //   setActiveItem(todo);
        // }
      });
      return cpyItemIndex;
    });
    // eslint-disable-next-line
  }, [relationships, contacts]);
};

export const useItemsMsgs = ({
  msgs = [],
  setItemIndex,
}: {
  msgs: any[];
  setItemIndex: React.Dispatch<React.SetStateAction<{ [key: number]: any }>>;
}) => {
  const navigate = useNavigate();
  return useEffect(() => {
    setItemIndex(itemIndex => {
      let cpyItemIndex = { ...itemIndex };
      Object.keys(cpyItemIndex).forEach((key: string) => {
        delete cpyItemIndex[parseInt(key)].msgs;
      });
      msgs?.forEach(msg => {
        let update = {
          // ...(cpyItemIndex[msg.item]?.msgs || {}),
          msgs: {
            num: (cpyItemIndex[msg.item]?.msgs?.num || 0) + 1,
            onClick: () =>
              navigate(
                `/project/${msg.project}/#i=${msg.item}&at=${
                  msg.type === 'Default' ? 'message' : 'event'
                }`
              ),
          },
        };
        cpyItemIndex[msg.item] = {
          ...(cpyItemIndex[msg.item] || {}),
          ...update,
        };
      });
      return cpyItemIndex;
    });
  }, [msgs]);
};

export const useItemsCmts = ({
  cmts = [],
  setItemIndex,
}: {
  cmts: any[];
  setItemIndex: React.Dispatch<React.SetStateAction<{ [key: number]: any }>>;
}) => {
  const navigate = useNavigate();
  return useEffect(() => {
    setItemIndex(itemIndex => {
      let cpyItemIndex = { ...itemIndex };
      Object.keys(cpyItemIndex).forEach((key: string) => {
        delete cpyItemIndex[parseInt(key)].cmts;
      });
      cmts?.forEach(cmt => {
        let update = {
          cmts: {
            num: (cpyItemIndex[cmt.item]?.cmts?.num || 0) + 1,
            onClick: () =>
              navigate(`/project/${cmt.project}/#i=${cmt.item}&at=comment`),
          },
        };
        cpyItemIndex[cmt.item] = {
          ...(cpyItemIndex[cmt.item] || {}),
          ...update,
        };
      });
      return cpyItemIndex;
    });
  }, [cmts]);
};

export function numFormatter(value: string | number) {
  const num = Number(`${value}`.replace(/,/g, ''));
  const absNum = Math.abs(num);
  if (absNum >= 1000 && absNum < 100000) {
    return Number((absNum / 100).toFixed(0)) >= 10
      ? (num / 1000).toFixed(1) + 'K'
      : num.toFixed(2); // convert to K for number from > 1000 < 1 million
  } else if (absNum >= 100000 && absNum < 1000000) {
    return Number((absNum / 1000).toFixed(0)) >= 1000
      ? (num / 1000000).toFixed(1) + 'M'
      : (num / 1000).toFixed(0) + 'K'; // convert to K for number from > 1000 < 1 million
  } else if (absNum >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M'; // convert to M for number from > 1 million
  } else if (absNum < 1000) {
    return num.toFixed(2); // if value < 1000, nothing to do
  }
}
